// Goal query/mutation hooks — per-client list + create, by-id update + delete.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { goalsApi, qk } from "../api";
import type { GoalInput } from "../api";

export function useGoals(clientId: number | undefined, activeOnly = true) {
  return useQuery({
    queryKey: qk.clients.goals(clientId ?? -1, activeOnly),
    queryFn: ({ signal }) =>
      goalsApi.list(clientId as number, activeOnly, signal),
    enabled: clientId !== undefined && clientId >= 0,
  });
}

export function useCreateGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, body }: { clientId: number; body: GoalInput }) =>
      goalsApi.create(clientId, body),
    onSuccess: (_g, { clientId }) => {
      void qc.invalidateQueries({ queryKey: ["clients", clientId, "goals"] });
      void qc.invalidateQueries({ queryKey: qk.goals.root });
    },
  });
}

export function useUpdateGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ goalId, body }: { goalId: number; body: GoalInput }) =>
      goalsApi.update(goalId, body),
    onSuccess: () => {
      // The goal id alone doesn't identify the client list, so invalidate the
      // goals + clients roots (partial match refetches the affected lists).
      void qc.invalidateQueries({ queryKey: qk.goals.root });
      void qc.invalidateQueries({ queryKey: qk.clients.root });
    },
  });
}

export function useDeleteGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (goalId: number) => goalsApi.remove(goalId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.goals.root });
      void qc.invalidateQueries({ queryKey: qk.clients.root });
    },
  });
}
